import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import FileViewer from '@/components/FileViewer';
import MesDonnees from '@/components/MesDonnees';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { User as UserIcon, Save, Download, Trash2, Eye, X, Camera, Lock, KeyRound, AlertCircle, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mêmes codes interdits que côté serveur (chiffres identiques, suites
// croissantes/décroissantes) : vérification côté client pour un retour
// immédiat, le serveur revalide systématiquement.
const EASY_PINS = new Set();
for (let d = 0; d <= 9; d++) EASY_PINS.add(String(d).repeat(4));
for (let start = 0; start <= 6; start++) {
  const seq = [0, 1, 2, 3].map((i) => String(start + i)).join('');
  EASY_PINS.add(seq);
  EASY_PINS.add(seq.split('').reverse().join(''));
}

const Profile = ({ user, onLogout }) => {
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    professional_card_number: '',
    phone: '',
    email: '',
    photo_url: '',
    photo_file_id: '',
    documents: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();

        // Enregistre immédiatement la photo sur le profil, sans attendre que
        // l'utilisateur clique sur "Enregistrer" (c'était la cause du bug :
        // la photo restait seulement en mémoire tant que ce bouton n'était
        // pas cliqué, et était donc perdue si on quittait la page avant).
        const saveResponse = await fetch(`${BACKEND_URL}/api/profile/${user.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ photo_file_id: data.id })
        });

        if (saveResponse.ok) {
          setProfile(prev => ({ ...prev, photo_file_id: data.id }));

          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => setPhotoPreview(reader.result);
          reader.readAsDataURL(file);

          toast.success('Photo de profil enregistrée');
        } else {
          toast.error('La photo a été importée mais pas enregistrée sur le profil');
        }
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const loadProfilePhoto = async (fileId) => {
    if (!fileId) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPhotoPreview(url);
      }
    } catch (error) {
      console.error('Error loading photo:', error);
    }
  };

  const handleViewDocument = async (fileId, index) => {
    const token = localStorage.getItem('token');
    setPreviewLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const contentType = response.headers.get('content-type') || blob.type;
        const url = window.URL.createObjectURL(blob);
        
        // Pour les fichiers texte, lire le contenu
        let textContent = null;
        if (contentType?.startsWith('text/') || contentType?.includes('json')) {
          textContent = await blob.text();
        }

        // Pour les PDF, créer un data URL (fonctionne mieux dans les modals)
        let pdfDataUrl = null;
        if (contentType === 'application/pdf') {
          const reader = new FileReader();
          pdfDataUrl = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        }
        
        setPreviewDoc({ 
          url, 
          contentType, 
          name: `Document ${index + 1}`, 
          fileId,
          textContent,
          pdfDataUrl,
          blob
        });
      } else {
        toast.error('Erreur lors du chargement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewDoc?.url) {
      window.URL.revokeObjectURL(previewDoc.url);
    }
    setPreviewDoc(null);
  };
  
  const handleDownloadFromPreview = () => {
    if (previewDoc?.url) {
      const a = document.createElement('a');
      a.href = previewDoc.url;
      a.download = previewDoc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Document téléchargé');
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          ...data,
          first_name: data.first_name || '',
          last_name: data.last_name || ''
        });
        // Load profile photo if exists
        if (data.photo_file_id) {
          loadProfilePhoto(data.photo_file_id);
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        toast.success('Profil mis à jour');
        loadProfile();
      } else {
        toast.error('Erreur de sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Modification du mot de passe
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Merci de saisir votre mot de passe actuel');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Le nouveau mot de passe doit comporter au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Les deux mots de passe ne correspondent pas');
      return;
    }
    setPasswordSaving(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      if (response.ok) {
        toast.success('Mot de passe modifié');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const err = await response.json();
        setPasswordError(err.detail || 'Erreur lors de la modification');
      }
    } catch {
      setPasswordError('Erreur de connexion, merci de réessayer');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Modification du code personnel (agents et chefs de poste uniquement)
  // Un compte de site (partagé) ne peut modifier ni son mot de passe ni un
  // code personnel lui-même : seul un administrateur peut le faire pour lui.
  // Un compte client peut modifier son mot de passe, mais n'a pas de code
  // personnel (il ne rédige jamais d'événement en tant que rédacteur).
  const canManagePassword = user?.role !== 'site';
  const canManagePin = ['agent', 'chef_poste', 'admin'].includes(user?.role);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState('');
  const sanitizePin = (value) => value.replace(/\D/g, '').slice(0, 4);

  const handleChangePin = async () => {
    setPinError('');
    if (user?.pin_set && !/^\d{4}$/.test(currentPin)) {
      setPinError('Merci de saisir votre code actuel');
      return;
    }
    if (!/^\d{4}$/.test(newPin)) {
      setPinError('Le nouveau code doit comporter exactement 4 chiffres');
      return;
    }
    if (EASY_PINS.has(newPin)) {
      setPinError('Ce code est trop simple (chiffres identiques ou suite logique comme 1234) : choisissez-en un autre');
      return;
    }
    if (newPin !== confirmPin) {
      setPinError('Les deux codes saisis ne correspondent pas');
      return;
    }
    setPinSaving(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/set-pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: newPin, current_pin: currentPin || undefined })
      });
      if (response.ok) {
        toast.success('Code personnel modifié');
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
      } else {
        const err = await response.json();
        setPinError(err.detail || 'Erreur lors de la modification');
      }
    } catch {
      setPinError('Erreur de connexion, merci de réessayer');
    } finally {
      setPinSaving(false);
    }
  };

  // Sites d'origine des notifications (consignes à signer, messages d'équipe) -
  // un admin voit normalement tous les sites, y compris ceux auxquels il n'est
  // pas personnellement affecté ; cette préférence lui permet de ne suivre que
  // certains sites. notification_site_ids null/absent = aucune restriction.
  const canManageNotificationSites = user?.role === 'admin';
  const [notifSites, setNotifSites] = useState([]);
  const [notifScope, setNotifScope] = useState('all'); // 'all' | 'selected'
  const [notifSelectedSites, setNotifSelectedSites] = useState([]);
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    if (!canManageNotificationSites) return;
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setNotifSites(await res.json());
      } catch { /* silencieux */ }
    })();
    if (user?.notification_site_ids) {
      setNotifScope('selected');
      setNotifSelectedSites(user.notification_site_ids);
    } else {
      setNotifScope('all');
      setNotifSelectedSites([]);
    }
    // eslint-disable-next-line
  }, [canManageNotificationSites, user?.notification_site_ids]);

  const handleSaveNotificationSettings = async () => {
    if (notifScope === 'selected' && notifSelectedSites.length === 0) {
      toast.warning('Sélectionnez au moins un site, ou choisissez "Tous les sites"');
      return;
    }
    setNotifSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_site_ids: notifScope === 'selected' ? notifSelectedSites : null })
      });
      if (res.ok) {
        toast.success('Préférences de notifications enregistrées');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setNotifSaving(false);
    }
  };

  const [newDocName, setNewDocName] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const handleUploadNamedDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!newDocName.trim()) {
      toast.error('Donnez un nom au document avant de l\'importer');
      e.target.value = '';
      return;
    }
    setUploadingDoc(true);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const fileInfo = await response.json();
        const updatedDocs = [...(profile.documents || []), { file_id: fileInfo.id, name: newDocName.trim() }];
        setProfile({ ...profile, documents: updatedDocs });
        setNewDocName('');
        toast.success('Document ajouté (cliquez sur Enregistrer pour confirmer)');
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  // Récupère l'identifiant de fichier et le nom d'un document, qu'il soit au
  // nouveau format {file_id, name} ou à l'ancien format (simple chaîne de
  // caractères), pour rester compatible avec les documents déjà enregistrés.
  const getDocFileId = (doc) => (typeof doc === 'string' ? doc : doc.file_id);
  const getDocName = (doc, index) => (typeof doc === 'string' ? `Document ${index + 1}` : (doc.name || `Document ${index + 1}`));

  const handleRemoveDocument = (doc) => {
    const targetId = getDocFileId(doc);
    const updatedDocs = profile.documents.filter(d => getDocFileId(d) !== targetId);
    setProfile({ ...profile, documents: updatedDocs });
    toast.success('Document retiré (cliquez sur Enregistrer pour confirmer)');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="profile">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Mon Profil
          </h1>
          <p className="text-gray-600 dark:text-slate-300">Informations personnelles et professionnelles</p>
        </div>

        <Card className="glass-effect">
          <CardHeader>
            <div className="flex items-center gap-4">
              {/* Photo de profil */}
              <div className="relative">
                <div 
                  className="w-20 h-20 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center cursor-pointer border-4 border-white shadow-lg"
                  onClick={() => photoInputRef.current?.click()}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Photo de profil" className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon className="h-10 w-10 text-blue-600" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-blue-700 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <div>
                <CardTitle>Informations personnelles</CardTitle>
                <p className="text-sm text-gray-600 dark:text-slate-300">Cliquez sur la photo pour la modifier</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date de naissance</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={profile.date_of_birth || ''}
                  onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="professional_card_number">N° Carte Professionnelle</Label>
                <Input
                  id="professional_card_number"
                  value={profile.professional_card_number || ''}
                  onChange={(e) => setProfile({ ...profile, professional_card_number: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profile.phone || ''}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email || ''}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Documents (diplômes, carte pro, etc.)</Label>
              
              {/* Affichage des documents existants */}
              {profile.documents && profile.documents.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-slate-200">Documents attachés :</p>
                  <div className="space-y-2">
                    {profile.documents.map((doc, index) => {
                      const fileId = getDocFileId(doc);
                      const docName = getDocName(doc, index);
                      return (
                      <div
                        key={fileId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded border hover:bg-gray-100 dark:bg-slate-700 transition-colors"
                      >
                        <div 
                          className="flex items-center gap-3 flex-1 cursor-pointer"
                          onClick={() => handleViewDocument(fileId, index)}
                          title="Cliquer pour visualiser"
                        >
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                            <span className="text-xl">📄</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-slate-100 hover:text-blue-600">
                              {docName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Cliquer pour visualiser</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDocument(fileId, index)}
                            title="Visualiser"
                            data-testid={`view-doc-${index}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                const response = await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = docName;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                  toast.success('Document téléchargé');
                                } else {
                                  toast.error('Erreur téléchargement');
                                }
                              } catch (error) {
                                toast.error('Erreur de connexion');
                              }
                            }}
                            title="Télécharger"
                            data-testid={`download-doc-${index}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveDocument(doc)}
                            title="Retirer ce document"
                            data-testid={`delete-doc-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Upload de nouveaux documents, avec un nom personnalisé */}
              <div className="border-t pt-3 space-y-2">
                <p className="text-sm text-gray-600 dark:text-slate-300 mb-1">Ajouter un nouveau document :</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Nom du document (ex: Carte professionnelle)"
                    value={newDocName}
                    onChange={(e) => setNewDocName(e.target.value)}
                    className="flex-1"
                    data-testid="new-doc-name"
                  />
                  <div>
                    <input
                      type="file"
                      id="doc-upload-input"
                      onChange={handleUploadNamedDocument}
                      className="hidden"
                      disabled={uploadingDoc}
                      data-testid="doc-upload-input"
                    />
                    <label htmlFor="doc-upload-input">
                      <Button type="button" variant="outline" disabled={uploadingDoc} asChild>
                        <span className="cursor-pointer whitespace-nowrap">
                          {uploadingDoc ? 'Import en cours...' : 'Choisir un fichier'}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500">Donnez d'abord un nom, puis choisissez le fichier à importer.</p>
              </div>
            </div>

            <Button onClick={handleSave} className="btn-primary w-full" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </CardContent>
        </Card>

        {/* Sécurité : mot de passe et code personnel */}
        <Card className="glass-effect">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300" />
              </div>
              <CardTitle>Sécurité</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {!canManagePassword && (
              <p className="text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid="no-self-service-note">
                Ce compte partagé ne peut pas modifier ses identifiants lui-même : seul un administrateur
                peut le faire, depuis l'espace Administration.
              </p>
            )}

            {/* Mot de passe */}
            {canManagePassword && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Mot de passe</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Mot de passe actuel *</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(''); }}
                      data-testid="current-password-input"
                    />
                  </div>
                  <div />
                  <div className="space-y-2">
                    <Label htmlFor="new_password">Nouveau mot de passe *</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                {passwordError && (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-sm" data-testid="password-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{passwordError}</span>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={handleChangePassword}
                  disabled={passwordSaving}
                  data-testid="change-password-btn"
                >
                  {passwordSaving ? 'Modification...' : 'Modifier le mot de passe'}
                </Button>
              </div>
            )}

            {/* Code personnel (agents, chefs de poste et administrateurs uniquement) */}
            {canManagePin && (
              <div className="space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-1.5">
                    <KeyRound className="h-4 w-4 text-indigo-600" />
                    Code personnel
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    Ce code à 4 chiffres confirme votre identité lorsque vous êtes désigné comme agent
                    rédacteur sur un compte de site partagé.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {user?.pin_set && (
                    <div className="space-y-2">
                      <Label htmlFor="current_pin">Code actuel *</Label>
                      <Input
                        id="current_pin"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={currentPin}
                        onChange={(e) => { setCurrentPin(sanitizePin(e.target.value)); setPinError(''); }}
                        placeholder="••••"
                        className="text-center tracking-[0.5em]"
                        data-testid="current-pin-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="new_pin">Nouveau code *</Label>
                    <Input
                      id="new_pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={newPin}
                      onChange={(e) => { setNewPin(sanitizePin(e.target.value)); setPinError(''); }}
                      placeholder="••••"
                      className="text-center tracking-[0.5em]"
                      data-testid="new-pin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_pin">Confirmer le code *</Label>
                    <Input
                      id="confirm_pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={confirmPin}
                      onChange={(e) => { setConfirmPin(sanitizePin(e.target.value)); setPinError(''); }}
                      placeholder="••••"
                      className="text-center tracking-[0.5em]"
                      data-testid="confirm-pin-input"
                    />
                  </div>
                </div>

                {pinError && (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-sm" data-testid="pin-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{pinError}</span>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={handleChangePin}
                  disabled={pinSaving}
                  data-testid="change-pin-btn"
                >
                  {pinSaving ? 'Modification...' : (user?.pin_set ? 'Modifier le code personnel' : 'Créer mon code personnel')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* RGPD : export, rectification, charte signée */}
        <MesDonnees user={user} />

        {/* Sites suivis pour les notifications - admin uniquement, puisque
            c'est le seul rôle qui reçoit par défaut les notifications de tous
            les sites même sans y être affecté. */}
        {canManageNotificationSites && (
          <Card className="glass-effect">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <Bell className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                </div>
                <CardTitle>Notifications</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-slate-400">
                En tant qu'administrateur, vous recevez par défaut les notifications (consignes à signer,
                messages d'équipe) de tous les sites. Vous pouvez ne suivre que certains sites en particulier.
              </p>

              <RadioGroup value={notifScope} onValueChange={setNotifScope} className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="all" data-testid="notif-scope-all" />
                  Tous les sites
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="selected" data-testid="notif-scope-selected" />
                  Uniquement les sites sélectionnés ci-dessous
                </label>
              </RadioGroup>

              {notifScope === 'selected' && (
                <div className="border rounded-xl p-3 max-h-48 overflow-y-auto space-y-1.5 bg-slate-50/50 dark:bg-slate-800">
                  {notifSites.length === 0 ? (
                    <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">Aucun site créé</p>
                  ) : (
                    notifSites.map((site) => (
                      <div key={site.id} className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors">
                        <Checkbox
                          id={`notif-site-${site.id}`}
                          checked={notifSelectedSites.includes(site.id)}
                          onCheckedChange={(checked) => setNotifSelectedSites((prev) => (
                            checked ? [...prev, site.id] : prev.filter((id) => id !== site.id)
                          ))}
                          data-testid={`notif-site-${site.id}`}
                        />
                        <label htmlFor={`notif-site-${site.id}`} className="text-sm cursor-pointer flex-1">
                          {site.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}

              <Button
                variant="outline"
                onClick={handleSaveNotificationSettings}
                disabled={notifSaving}
                data-testid="save-notification-settings-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {notifSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de prévisualisation de document */}
      {previewDoc && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={closePreview}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50 dark:bg-slate-800 shrink-0">
              <h3 className="font-semibold text-base sm:text-lg truncate pr-2">{previewDoc.name}</h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadFromPreview}
                  className="hidden sm:flex"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Télécharger
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadFromPreview}
                  className="sm:hidden"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePreview}
                  className="hover:bg-gray-200"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-100 dark:bg-slate-700">
              {/* Images */}
              {previewDoc.contentType?.startsWith('image/') && (
                <div className="flex items-center justify-center min-h-[200px]">
                  <img 
                    src={previewDoc.url} 
                    alt={previewDoc.name}
                    className="max-w-full max-h-[70vh] h-auto rounded shadow-lg"
                  />
                </div>
              )}
              
              {/* PDF */}
              {previewDoc.contentType === 'application/pdf' && (
                <div className="bg-white dark:bg-slate-800 rounded shadow h-full">
                  <iframe
                    src={previewDoc.pdfDataUrl || previewDoc.url}
                    title={previewDoc.name}
                    className="w-full rounded border-0"
                    style={{ height: '70vh', minHeight: '400px' }}
                  />
                </div>
              )}
              
              {/* Text files */}
              {(previewDoc.contentType?.startsWith('text/') || previewDoc.contentType?.includes('json')) && previewDoc.textContent && (
                <div className="bg-white dark:bg-slate-800 rounded shadow p-4 overflow-auto" style={{ maxHeight: '70vh' }}>
                  <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 dark:text-slate-100 leading-relaxed">
                    {previewDoc.textContent}
                  </pre>
                </div>
              )}
              
              {/* Other files */}
              {!previewDoc.contentType?.startsWith('image/') && 
               previewDoc.contentType !== 'application/pdf' && 
               !previewDoc.contentType?.startsWith('text/') && 
               !previewDoc.contentType?.includes('json') && (
                <div className="bg-white dark:bg-slate-800 rounded shadow p-8 text-center">
                  <div className="text-6xl mb-4">📎</div>
                  <p className="text-gray-800 dark:text-slate-100 font-medium mb-2">
                    {previewDoc.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                    Type: {previewDoc.contentType || 'Fichier'}
                  </p>
                  <Button onClick={handleDownloadFromPreview} className="btn-primary">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger le fichier
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {previewLoading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Chargement...</span>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Profile;
